const FACEBOOK_PIXEL_ID = '841250880932121';
const CLARITY_PROJECT_ID = 'lmv80tbir2';
const GTM_CONTAINER_ID = 'GTM-KVJ4GJS';

let hasScheduledThirdPartyScripts = false;

type FacebookQueueItem = unknown[];
type FacebookQueueFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  push?: (...args: unknown[]) => void;
  queue?: FacebookQueueItem[];
  version?: string;
};

const appendScriptOnce = (
  id: string,
  src: string,
  target: ParentNode = document.head,
) => {
  const existingScript = document.getElementById(id);
  if (existingScript) {
    return existingScript as HTMLScriptElement;
  }

  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  target.appendChild(script);

  return script;
};

const runWhenBrowserIsIdle = (callback: () => void) => {
  if (typeof window === 'undefined') {
    return;
  }

  const scheduleCallback = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => callback(), {
        timeout: 2000,
      });
      return;
    }

    (window as Window).setTimeout(callback, 1200);
  };

  if (document.readyState === 'complete') {
    scheduleCallback();
    return;
  }

  window.addEventListener('load', scheduleCallback, {
    once: true,
  });
};

const setupFacebookPixelStub = () => {
  const existingFbq = window.fbq as FacebookQueueFunction | undefined;
  if (existingFbq?.loaded) {
    return;
  }

  if (existingFbq) {
    return;
  }

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
      return;
    }

    fbq.queue?.push(args);
  }) as FacebookQueueFunction;

  fbq.push = (...args: unknown[]) => {
    fbq.queue?.push(args);
  };
  fbq.loaded = false;
  fbq.version = '2.0';
  fbq.queue = [];

  window.fbq = fbq;
  window._fbq = fbq;
  window.fbq('init', FACEBOOK_PIXEL_ID);
  window.fbq('track', 'PageView');
};

const setupClarityStub = () => {
  if (typeof window.clarity === 'function') {
    return;
  }

  window.clarity =
    window.clarity ||
    ((...args: unknown[]) => {
      window.clarity?.q?.push(args);
    });
  window.clarity.q = window.clarity.q || [];
};

const setupGtmDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  });
};

export const scheduleThirdPartyScripts = () => {
  if (typeof window === 'undefined' || hasScheduledThirdPartyScripts) {
    return;
  }

  hasScheduledThirdPartyScripts = true;
  setupFacebookPixelStub();
  setupClarityStub();
  setupGtmDataLayer();

  runWhenBrowserIsIdle(() => {
    appendScriptOnce(
      'facebook-pixel-script',
      'https://connect.facebook.net/en_US/fbevents.js',
    );
    appendScriptOnce(
      'microsoft-clarity-script',
      `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`,
    );
    appendScriptOnce(
      'google-tag-manager-script',
      `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`,
    );
  });
};
